import { cn } from '@/common/lib/cn/cn'

/**
 * Извлекает первые две буквы из названия, игнорируя организационно-правовые формы
 * @param name - название организации
 * @param options - дополнительные опции
 * @returns первые две буквы или пустая строка
 */
const getFirstTwoLetters = (
    name?: string,
    options: {
        caseSensitive?: boolean
        ignoreCase?: boolean
        maxLength?: number
    } = {}
): string => {
    const { caseSensitive = false, ignoreCase = true, maxLength = 2 } = options

    if (!name || typeof name !== 'string') {
        return ''
    }

    const legalForms = ['ИП', 'ООО', 'ОАО', 'ПАО', 'ЗАО', 'ТОО', 'АО']
    let processedName = name.trim()

    for (const form of legalForms) {
        const formToCheck = ignoreCase ? form.toUpperCase() : form
        const nameToCheck = ignoreCase ? processedName.toUpperCase() : processedName

        if (nameToCheck.startsWith(formToCheck)) {
            processedName = processedName.substring(form.length).trim()
            break
        }
    }
    const letters = processedName.substring(0, maxLength)
    return caseSensitive ? letters : letters.toUpperCase()
}

export type GenerateAvatarProps = {
    className?: string
    size?: 'sm' | 'md' | 'lg' | 'xl'
    placeholder?: string
    image?: string
}

export const Avatar = ({ className, size = 'md', placeholder, image }: GenerateAvatarProps) => {
    const avatarClasses = cn('avatar avatar-placeholder', className)

    const sizeClasses = cn(
        'bg-[var(--pixorion-light-gray)] text-[var(--pixorion-gray-2)] font-bold',
        'w-16 rounded-full',
        'flex items-center justify-center',
        {
            'w-[1.875rem] h-[1.875rem]': size === 'sm',
            'w-[2.5rem] h-[2.5rem]': size === 'md',
            'w-14 h-14': size === 'lg',
            'w-[8.75rem] h-[8.75rem]': size === 'xl',
        }
    )
    const sizeTextClasses = cn({
        'text-[0.75rem]': size === 'sm',
        '': size === 'md',
        'text-xl': size === 'lg',
        'text-[60px]': size === 'xl',
    })

    return (
        <div className={avatarClasses}>
            <div className={sizeClasses}>
                {placeholder && !image && <span className={sizeTextClasses}>{getFirstTwoLetters(placeholder)}</span>}
                {image && <img src={image} alt='' className='h-full w-full rounded-full object-cover' loading='lazy' />}
            </div>
        </div>
    )
}
